import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledBasicRRuleWrapper,
  StyledDateTimeInputWrapper,
  StyledNumberInput,
  StyledSelect,
} from './styles';
import {
  RRULE_FREQUENCY,
  type RRuleFrequency,
  getRRuleFromString,
  optionsToRRuleString,
  stringToRRule,
  type RRuleOptions,
} from '@kizen/rrule';
import { getFrequencyOptions } from '../helpers';
import { KizenTypography } from '__app/typography';
import { OutlineInputControl } from '__components/Inputs/InputControl';
import { useSelector } from 'react-redux';
import { getBusinessTimeZone } from 'store/authentication/selectors';
import DateTimeInput from '__components/Inputs/DateTimeInput';

export const BasicRRule: FC<{
  rruleString: string;
  onChange: (opts: { rrule: string; occurrences: Date[] }) => void;
  error: string | null;
  showMessage?: boolean;
}> = ({ rruleString, onChange, error, showMessage }) => {
  const { t } = useTranslation();

  const businessTimezone = useSelector(getBusinessTimeZone);

  const [{ freq, interval, dtstart }, setValues] = useState<
    Partial<RRuleOptions>
  >(() => {
    const rrule = rruleString ? stringToRRule(rruleString) : null;
    return {
      freq: rrule?.options.freq,
      dtstart: rrule?.options.dtstart,
      interval: rrule?.options.interval,
    };
  });

  useEffect(() => {
    const rrule = rruleString ? stringToRRule(rruleString) : null;
    setValues({
      freq: rrule?.options.freq,
      dtstart: rrule?.options.dtstart,
      interval: rrule?.options.interval,
    });
  }, [rruleString]);

  const onApplyChange = ({
    dtstart,
    interval,
    freq,
  }: Partial<RRuleOptions>) => {
    const opts = {
      freq: freq || RRULE_FREQUENCY.daily,
      interval: interval || 1,
      dtstart: dtstart || new Date(),
    };
    const rrule = optionsToRRuleString(opts);
    if (!dtstart || !interval) {
      setValues((prev) => ({
        ...prev,
        ...opts,
      }));
    }
    const { occurrences } = getRRuleFromString(rrule);
    onChange({ rrule, occurrences });
  };

  const handleChangeInterval = (v: number) => {
    setValues((prev) => ({ ...prev, interval: v }));
  };

  const handleBlurInterval = () => {
    onApplyChange({ freq, interval, dtstart });
  };

  const handleChangeFrequency = (v: { value: RRuleFrequency }) => {
    setValues((prev) => ({ ...prev, freq: Number(v.value) }));
    onApplyChange({ freq: Number(v.value), interval, dtstart });
  };

  const handleChangeDate = (date: Date | null) => {
    setValues((prev) => ({ ...prev, dtstart: date }));
    onApplyChange({ freq, interval, dtstart: date });
  };

  return (
    <OutlineInputControl label={t('Run every')} margin>
      <StyledBasicRRuleWrapper>
        <StyledNumberInput
          data-qa={'rrule-interval'}
          value={interval}
          onChange={handleChangeInterval}
          onBlur={handleBlurInterval}
          allowNegatives={false}
          error={!!error}
          validate={{ inModal: true, message: error, showMessage }}
        />
        <div data-qa={'rrule-freq'}>
          <StyledSelect
            value={String(freq)}
            onChange={handleChangeFrequency}
            options={getFrequencyOptions(t)}
            placeholder={t('Select Frequency')}
            error={!!error}
          />
        </div>
        <KizenTypography>{t('Starting At')}</KizenTypography>
        <StyledDateTimeInputWrapper>
          <DateTimeInput
            data-qa={'rrule-dtstart'}
            value={dtstart}
            onChange={handleChangeDate}
            businessTimezoneForDiscrepancyWarning={businessTimezone}
            error={!!error}
          />
        </StyledDateTimeInputWrapper>
      </StyledBasicRRuleWrapper>
    </OutlineInputControl>
  );
};
